import { useEffect, useState } from 'react';
import { Loader2, MessageSquare, AlertTriangle, FileText, CheckCircle2, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Alert, AlertDescription } from '@easyflow/ui-frontend/components/ui/alert';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { usePhoneChannelSenders } from './newConversation/useChannelSenders';
import { useCrmResolver } from './newConversation/useCrmResolver';
import type { MessagingTemplate, NewConversationDialogBaseProps } from './newConversation/types';

interface CreateConversationResponse {
  conversation_id: string;
}

export default function NewWhatsAppConversationDialog({ open, onClose, onSuccess }: NewConversationDialogBaseProps) {
  const { toast } = useToast();
  const { senders, defaultId } = usePhoneChannelSenders(open, 'whatsapp');
  const [templates, setTemplates] = useState<MessagingTemplate[]>([]);
  const [selectedSenderId, setSelectedSenderId] = useState<string>('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactName, setContactName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && defaultId && !selectedSenderId) setSelectedSenderId(defaultId);
  }, [open, defaultId, selectedSenderId]);

  useEffect(() => {
    if (!open) {
      setSelectedSenderId('');
      setContactPhone('');
      setContactName('');
      setSelectedTemplateId('');
      setTemplateVariables({});
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiClient
      .get<{ templates: MessagingTemplate[] }>('/messaging/templates')
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data?.templates) setTemplates(res.data.templates);
      })
      .catch(() => { if (!cancelled) setTemplates([]); });
    return () => { cancelled = true; };
  }, [open]);

  const { resolved, resolving } = useCrmResolver(contactPhone, open);

  useEffect(() => {
    if (resolved && !contactName) {
      const name = resolved.variables.customer_full_name;
      if (name) setContactName(name);
    }
  }, [resolved, contactName]);

  useEffect(() => {
    if (!selectedTemplateId) return;
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (!template) return;
    const numericVars = template.body_text.match(/\{\{(\d+)\}\}/g) || [];
    const namedVars = template.body_text.match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
    const initialVars: Record<string, string> = {};
    numericVars.forEach((v) => { initialVars[v.replace(/[{}]/g, '')] = ''; });
    namedVars.forEach((v) => {
      const name = v.replace(/[{}]/g, '');
      initialVars[name] = resolved?.variables[name] || '';
    });
    setTemplateVariables(initialVars);
  }, [selectedTemplateId, templates, resolved]);

  const selectedSender = senders.find((s) => s.id === selectedSenderId) || null;
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || null;

  const renderTemplatePreview = (): string => {
    if (!selectedTemplate) return '';
    let preview = selectedTemplate.body_text;
    Object.entries(templateVariables).forEach(([key, value]) => {
      preview = preview.replace(`{{${key}}}`, value || `[${key}]`);
    });
    return preview;
  };

  const handleSubmit = async () => {
    if (!selectedSender) {
      toast({ title: 'Errore', description: 'Seleziona un mittente WhatsApp', variant: 'destructive' });
      return;
    }
    if (!contactPhone) {
      toast({ title: 'Errore', description: 'Inserisci il numero del contatto', variant: 'destructive' });
      return;
    }
    if (!selectedTemplateId) {
      toast({ title: 'Errore', description: 'Seleziona un template approvato', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiClient.post<CreateConversationResponse>('/messaging/conversations', {
        contact_phone: contactPhone.startsWith('+') ? contactPhone : `+${contactPhone}`,
        contact_name: contactName || undefined,
        channel: 'whatsapp',
        from_number: selectedSender.phone_number,
        template_id: selectedTemplateId,
        template_variables: templateVariables,
      });
      if (res.success && res.data?.conversation_id) {
        toast({ title: 'Conversazione WhatsApp avviata' });
        onSuccess(res.data.conversation_id);
        onClose();
      } else {
        toast({ title: 'Errore', description: res.error || 'Impossibile avviare la conversazione', variant: 'destructive' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Nuova Conversazione WhatsApp
          </DialogTitle>
          <DialogDescription>Avvia un WhatsApp tramite template approvato.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Numero Mittente *</Label>
            <Select value={selectedSenderId} onValueChange={setSelectedSenderId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={senders.length === 0 ? 'Nessun numero WhatsApp configurato' : 'Seleziona numero mittente'} />
              </SelectTrigger>
              <SelectContent>
                {senders.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="flex items-center gap-2">
                      <MessageSquare className="h-3.5 w-3.5" />
                      {s.phone_number}
                      {s.label && <span className="text-muted-foreground">({s.label})</span>}
                      {s.isDefault && <Badge variant="outline" className="ml-1">Default</Badge>}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Numero Contatto *</Label>
            <div className="relative mt-1">
              <Input
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+39 333 1234567"
                className="pr-10"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                {resolving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                {!resolving && resolved?.source.customer && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                {!resolving && resolved?.source.lead && <User className="h-4 w-4 text-blue-500" />}
              </div>
            </div>
            {resolved && (resolved.source.customer || resolved.source.lead) && (
              <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                {resolved.source.customer ? (
                  <><CheckCircle2 className="h-3 w-3 text-green-500" /> Cliente trovato ({resolved.source.customer.type.toUpperCase()})</>
                ) : resolved.source.lead ? (
                  <><User className="h-3 w-3 text-blue-500" /> Lead trovato: {resolved.source.lead.title}</>
                ) : null}
              </div>
            )}
          </div>

          <div>
            <Label>Nome Contatto (opzionale)</Label>
            <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Mario Rossi" className="mt-1" />
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>Per aprire una conversazione WhatsApp è necessario un template approvato (policy 24h).</AlertDescription>
          </Alert>

          <div>
            <Label>Template *</Label>
            <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={templates.length === 0 ? 'Nessun template approvato' : 'Seleziona template approvato'} />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {template.friendly_name}
                      <Badge variant="outline" className="ml-1">{template.category}</Badge>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTemplateId && Object.keys(templateVariables).length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Variabili Template</Label>
                {resolved && (resolved.source.customer || resolved.source.lead) && (
                  <Badge variant="secondary" className="text-xs">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Auto-compilate da CRM
                  </Badge>
                )}
              </div>
              {Object.keys(templateVariables).map((key) => {
                const isAutoFilled = resolved?.variables[key] && templateVariables[key] === resolved.variables[key];
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Badge
                      variant={isAutoFilled ? 'default' : 'outline'}
                      className={isAutoFilled ? 'bg-green-100 text-green-800 border-green-200' : ''}
                    >
                      {`{{${key}}}`}
                    </Badge>
                    <Input
                      value={templateVariables[key]}
                      onChange={(e) => setTemplateVariables((prev) => ({ ...prev, [key]: e.target.value }))}
                      placeholder={`Valore per {{${key}}}`}
                      className="flex-1"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {selectedTemplateId && (
            <div>
              <Label>Anteprima Messaggio</Label>
              <div className="mt-1 p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap">{renderTemplatePreview()}</div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Invia WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
